import React, { useState, useEffect, useMemo } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import Layout from '@/components/Layout';
import { fetchCollections, fetchCollectionWithProducts, CollectionWithProducts, slugToTag, fetchProducts, ShopifyProduct } from '@/services/shopify';
import CollectionSection from '@/components/Shop/CollectionSection';
import { ShopBreadcrumb } from '@/components/Shop/ShopBreadcrumb';
import { ShopCategoryNav } from '@/components/Shop/ShopCategoryNav';
import { Loader2 } from 'lucide-react';
import { Separator } from '@/components/ui/separator';
import { CurrencySelector } from '@/components/Shop/CurrencySelector';
import { Button } from '@/components/ui/button';
import { ProductCard } from '@/components/Shop/ProductCard';
import { FeaturedProductsCarousel } from '@/components/Shop/FeaturedProductsCarousel';
const Shop = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const activeCategory = searchParams.get('category');
  const [collections, setCollections] = useState<CollectionWithProducts[]>([]);
  const [products, setProducts] = useState<ShopifyProduct[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  useEffect(() => {
    const loadData = async () => {
      try {
        setIsLoading(true);
        if (activeCategory) {
          // Load products directly by tag for category filtering
          const searchTag = slugToTag(activeCategory);
          const allProducts = await fetchProducts(100, `tag:${searchTag}`);
          setProducts(allProducts);
        } else {
          // Load collections for "Shop by Brand" view
          const allCollections = await fetchCollections(20);
          const collectionsWithProducts = await Promise.all(allCollections.map(async collection => {
            const collectionData = await fetchCollectionWithProducts(collection.node.handle, 12);
            return collectionData;
          }));
          const validCollections = collectionsWithProducts.filter((col): col is CollectionWithProducts => col !== null && col.products.edges.length > 0);
          setCollections(validCollections);
        }
      } catch (error) {
        console.error('Error loading data:', error);
      } finally {
        setIsLoading(false);
      }
    };
    loadData();
  }, [activeCategory]);

  // Get category title for display
  const categoryTitle = activeCategory ? slugToTag(activeCategory).charAt(0).toUpperCase() + slugToTag(activeCategory).slice(1).toLowerCase() : 'Shop by Brand';

  // Scroll to top when category changes
  useEffect(() => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  }, [activeCategory]);
  return <Layout>
      <div className="min-h-screen pb-20">
        {/* Category Navigation */}
        <ShopCategoryNav activeCategory={activeCategory || undefined} />
        
        <div className="max-w-screen-2xl mx-auto px-4 md:px-8">
          {/* Breadcrumb Navigation */}
          <div className="mt-2 mb-4">
            <ShopBreadcrumb items={[{
            label: 'Shop'
          }]} />
          </div>

          {/* Featured Products Carousel - Only show on main shop page */}
          {!activeCategory && !isLoading && <FeaturedProductsCarousel />}

          {/* Page Header */}
          <div className="mb-4 flex items-start justify-between my-[21px]">
            <div>
              <h1 className="mb-4 md:text-3xl font-normal text-2xl">{categoryTitle}</h1>
              {activeCategory ? <p className="text-muted-foreground text-base">
                  Browse our {slugToTag(activeCategory).toLowerCase()} collection
                </p> : !isLoading && collections.length > 0 && <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-x-4 gap-y-2 text-sm">
                    {collections.map(collection => <button key={collection.id} onClick={() => navigate(`/shop/collection/${collection.handle}`)} className="text-left text-muted-foreground hover:text-foreground transition-colors">
                        {collection.title}
                      </button>)}
                  </div>}
            </div>
            <div className="hidden md:block">
              <CurrencySelector />
            </div>
          </div>

          {/* Loading State */}
          {isLoading && <div className="flex items-center justify-center py-20">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>}

          {/* Category Grid View */}
          {!isLoading && activeCategory && products.length > 0 && <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
              {products.map(product => <ProductCard key={product.node.id} product={product} />)}
            </div>}

          {/* Collections Sections View */}
          {!isLoading && !activeCategory && collections.length > 0 && <div className="space-y-16">
              {collections.map((collection, index) => <React.Fragment key={collection.id}>
                  <CollectionSection collection={collection} />
                  {index < collections.length - 1 && <Separator className="my-8" />}
                </React.Fragment>)}
            </div>}

          {/* Empty State */}
          {!isLoading && activeCategory && products.length === 0 && <div className="text-center py-20">
              <p className="text-lg text-muted-foreground mb-4">
                No products found in {slugToTag(activeCategory)}
              </p>
              <Button variant="outline" onClick={() => navigate('/shop')}>
                View All Products
              </Button>
            </div>}

          {!isLoading && !activeCategory && collections.length === 0 && <div className="text-center py-20">
              <p className="text-lg text-muted-foreground">
                No collections found. Please add collections in your Shopify admin.
              </p>
            </div>}
        </div>
      </div>
    </Layout>;
};
export default Shop;